import { Box, Container, Divider, Typography } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import Button from '@/components/button'
import { useCreateDiagnosis } from '@/hooks/mutations/use-create-diagnosis'
import { DefaultSelectFilesFormData } from '@/pages/demands/steps/select-files/hooks/use-select-files-form'
import { routes } from '@/routes/config'

import { useDiagnosisRegistrationStore } from '../../contexts/diagnosis-registration-store-context'
import { bytesToUnitString } from '../../utils/string-conversions'
import { LinearProgressWithLabel } from './components/linear-progress-with-label'
import { useStyles } from './styles'

type FilesProgress =
  DefaultSelectFilesFormData[keyof DefaultSelectFilesFormData]

function makeInitialFilesProgress(selectFiles: DefaultSelectFilesFormData) {
  const initialState = Object.entries<FilesProgress>(selectFiles).map(
    ([name, value]) => {
      const input = value?.input.reduce(
        (acc, file) => {
          acc.size += file.size
          acc.label = acc.label ? [acc.label, file.name].join(', ') : file.name

          return acc
        },
        {
          size: 0,
          label: '',
        },
      )

      return {
        inputType: name,
        input: input,
        currentSize: 0,
        progress: 0,
      }
    },
  )

  return initialState
}

export const UploadFiles = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const { registerData, selectFilesData } = useDiagnosisRegistrationStore(
    (state) => ({
      registerData: state.registerData,
      selectFilesData: state.selectFilesData,
    }),
  )

  const [filesProgress, setFilesProgress] = useState(() => {
    const selectFiles = selectFilesData
    if (!selectFiles) return

    return makeInitialFilesProgress(selectFiles)
  })

  const hasData = !!(registerData && selectFilesData)

  const { mutate, isSuccess, isError } = useCreateDiagnosis({
    onUploadProgressByInput: handleUploadProgressByInput,
  })

  function handleUploadProgressByInput(
    inputType: string,
    { progress, length }: { progress?: number; length?: number },
  ) {
    setFilesProgress((state) =>
      state?.map((file) => {
        if (file.inputType === inputType) {
          if (length) {
            const currentSize = file.currentSize + length
            const progress = Math.floor((currentSize / file.input!.size) * 100)

            return { ...file, currentSize, progress }
          }

          return { ...file, progress: Number(progress) }
        }
        return file
      }),
    )
  }

  function navigateToFinish() {
    if (isError) {
      resetProgress()
      return mutate()
    }
    navigate(routes.Demands('finish'), {
      replace: true,
    })
  }

  function resetProgress() {
    setFilesProgress((state) =>
      state?.map((file) => ({ ...file, currentSize: 0 })),
    )
  }

  useEffect(() => {
    if (!hasData) return

    mutate()
  }, [])

  if (!hasData) return <Navigate to={routes.Demands('register')} replace />

  return (
    <Container className={classes.main}>
      <Box>
        <Typography variant={'h1'}>
          Upload de arquivos de processamento
        </Typography>
        <Typography variant={'h4'}>Arquivos de processamento</Typography>
        <Box className={classes.container}>
          {filesProgress!.map((file) => (
            <LinearProgressWithLabel
              key={file.inputType}
              label={file.input!.label}
              progressValue={file.progress}
              fileSize={bytesToUnitString(file.input!.size)}
              isError={isError}
            />
          ))}
        </Box>
      </Box>
      <Divider />
      <Box className={classes.buttonContainer}>
        <Button outlined onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button disabled={!(isError || isSuccess)} onClick={navigateToFinish}>
          {isError ? 'Tentar novamente' : 'Avançar'}
        </Button>
      </Box>
    </Container>
  )
}
